import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import ScrollFadeWrapper, {
  ScrollFadeSection,
} from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import LinkWithIconComponent from '../LinkWithIcon/LinkWithIconComponent';
import Image from '../../Image/Image';

// -------------------------
// 🔗 R2 SVG URLs
// -------------------------
const TICK_ICON = 'https://assets.askhippo.co.za/Icons/common/TickCircle.svg';

const CROSS_ICON =
  'https://assets.askhippo.co.za/Icons/common/Delete-Square-New.svg';

const CROSS_ICON_BLACK =
  'https://assets.askhippo.co.za/Icons/common/Delete-Square-New-Black.svg';

const ARROW_ICON = 'https://assets.askhippo.co.za/Icons/common/IconArrow.svg';

const TEXT_DARK = '#162F3B';
const TEXT_GREY = '#A2ACB1';

const normalizeIconType = (type) => String(type || '').toLowerCase();

const getListItemColor = (type, crossColourBlack) => {
  const normalized = normalizeIconType(type);

  if (normalized === 'cross' || normalized === 'untick') {
    return crossColourBlack ? TEXT_DARK : TEXT_GREY;
  }

  return TEXT_DARK;
};

// -------------------------
// Icon Selector
// -------------------------
const IconSelector = ({ type, crossColourBlack }) => {
  const normalizedType = normalizeIconType(type);

  if (normalizedType === 'tick')
    return (
      <Image
        src={TICK_ICON}
        alt="Yes"
        width={24}
        height={24}
        sx={{ width: 24, height: 24, flexShrink: 0 }}
      />
    );

  if (normalizedType === 'cross' || normalizedType === 'untick')
    return (
      <Image
        src={crossColourBlack ? CROSS_ICON_BLACK : CROSS_ICON}
        alt="No"
        width={24}
        height={24}
        sx={{ width: 24, height: 24, flexShrink: 0 }}
      />
    );

  return null;
};

// -------------------------
//  Component
// -------------------------
const ThreeColumnCardComponent = ({ data }) => {
  const { title, description, footer, cards, footerLink, crossColourBlack } =
    data;
  const navigate = useNavigate();
  const buttonRefs = useRef([]);
  const cardRefs = useRef([]);
  const headerRefs = useRef([]);
  const [equalCardHeight, setEqualCardHeight] = useState(null);
  const [equalHeaderHeight, setEqualHeaderHeight] = useState(null);
  const hasFooter = Boolean(String(footer || '').trim());

  useLayoutEffect(() => {
    const cardHeights = cardRefs.current
      .map((card) => {
        if (!card || !card.getBoundingClientRect) return 0;
        return card.getBoundingClientRect().height || 0;
      })
      .filter(Boolean);

    if (!cardHeights.length) return;

    const nextHeight = Math.ceil(Math.max(...cardHeights));
    setEqualCardHeight((currentHeight) =>
      currentHeight === nextHeight ? currentHeight : nextHeight
    );
  }, [cards]);

  useLayoutEffect(() => {
    const headerHeights = headerRefs.current
      .map((el) => {
        if (!el || !el.getBoundingClientRect) return 0;
        return el.getBoundingClientRect().height || 0;
      })
      .filter(Boolean);

    if (!headerHeights.length) return;

    const nextHeaderHeight = Math.ceil(Math.max(...headerHeights));
    setEqualHeaderHeight((current) =>
      current === nextHeaderHeight ? current : nextHeaderHeight
    );
  }, [cards, equalCardHeight]);

  // Bounce animation helper
  const gs_bounce = (target, strength = 0.85) => {
    if (!target) return Promise.resolve();

    return new Promise((resolve) => {
      const tl = gsap.timeline({
        onComplete: resolve,
        defaults: { transformOrigin: '50% 50%' },
      });

      tl.to(target, {
        scale: strength,
        duration: 0.15,
        ease: 'power3.inOut',
      }).to(target, {
        scale: 1,
        duration: 0.85,
        ease: 'elastic.out(1.75, 0.4)',
      });
    });
  };

  const handleButtonClick = async (index, link) => {
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!prefersReduced) {
      await gs_bounce(buttonRefs.current[index]);
    }
    if (link) navigate(link);
  };

  return (
    <ScrollFadeSection
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: '1010px',
          backgroundColor: '#E3E6E8',
          borderRadius: '48px',
          paddingTop: '32px',
          pb: '32px',
          px: '32px',
          boxSizing: 'border-box',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Typography
            component={data.component || 'h2'}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '3rem',
              lineHeight: '3.25rem',
              letterSpacing: '-0.03em',
              color: '#162F3B',
              whiteSpace: 'pre-line',
            }}
          >
            {title}
          </Typography>

          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: '1.5rem',
              lineHeight: '1.75rem',
              letterSpacing: '-0.03em',
              color: '#162F3B',
              whiteSpace: 'pre-line',
            }}
          >
            {description}
          </Typography>
        </Box>

        {/* Floating Cards */}
        <Box
          sx={{
            width: '1215px',
            alignSelf: 'center',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'stretch',
            flexWrap: 'nowrap',
            mt: '40px',
            gap: '24px',
          }}
        >
          {cards.map((card, index) => (
            <ScrollFadeWrapper key={index} delay={index * 0.15}>
              <Box
                ref={(el) => (cardRefs.current[index] = el)}
                sx={{
                  width: '389px',
                  flexShrink: 0,
                  backgroundColor: '#FFFFFF',
                  borderRadius: '48px',
                  pt: '104px',
                  pb: '80px',
                  height: equalCardHeight ? `${equalCardHeight}px` : 'auto',
                  boxSizing: 'border-box',
                  boxShadow:
                    '0px 1px 2px 0px rgba(162, 172, 177, 0.2), 0px 12px 24px -4px rgba(22, 47, 59, 0.12)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ px: '80px' }}>
                  <Box
                    ref={(el) => (headerRefs.current[index] = el)}
                    sx={{
                      minHeight: equalHeaderHeight
                        ? `${equalHeaderHeight}px`
                        : 'auto',
                    }}
                  >
                    <Typography
                      component={card.component || 'h3'}
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: '1.5rem',
                        lineHeight: '1.75rem',
                        letterSpacing: '-0.03em',
                        color: '#162F3B',
                        mb: '8px',
                        textAlign: 'left',
                      }}
                    >
                      {card.title}
                    </Typography>

                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 400,
                        fontSize: '0.875rem',
                        lineHeight: '1.25rem',
                        letterSpacing: '-0.02em',
                        color: '#162F3B',
                        textAlign: 'left',
                      }}
                    >
                      {card.body}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '16px',
                      mt: '40px',
                    }}
                  >
                    {card.listItems.map((item, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <IconSelector
                          type={item.icon}
                          crossColourBlack={crossColourBlack}
                        />
                        <Typography
                          sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 400,
                            fontSize: '1rem',
                            lineHeight: '1.5rem',
                            letterSpacing: '-0.02em',
                            color: getListItemColor(
                              item.icon,
                              crossColourBlack
                            ),
                            textAlign: 'left',
                          }}
                        >
                          {item.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Button */}
                {card.buttonText && (
                  <Box
                    sx={{
                      mt: '40px',
                      px: '80px',
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <Button
                      ref={(el) => (buttonRefs.current[index] = el)}
                      variant="contained"
                      disableElevation
                      endIcon={
                        <Image
                          src={ARROW_ICON}
                          alt="Compare quotes"
                          width={24}
                          height={24}
                          sx={{ width: 24, height: 24 }}
                        />
                      }
                      onClick={() => handleButtonClick(index, card.link)}
                      sx={{
                        width: 'fit-content',
                        minWidth: 0,
                        minHeight: '48px',
                        gap: '4px',
                        borderRadius: '24px',
                        px: '24px',
                        py: '12px',
                        backgroundColor: '#C7CA00',
                        textTransform: 'none',
                        willChange: 'transform',
                        '&:hover': { backgroundColor: '#b7bc00' },
                        '& .MuiButton-endIcon': { marginLeft: '8px' },
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 700,
                          fontSize: '1rem',
                          lineHeight: '1.5rem',
                          letterSpacing: '-0.03em',
                          color: '#162F3B',
                        }}
                      >
                        {card.buttonText}
                      </Typography>
                    </Button>
                  </Box>
                )}
              </Box>
            </ScrollFadeWrapper>
          ))}
        </Box>

        {hasFooter ? (
          <Box sx={{ mt: '40px', display: 'flex', justifyContent: 'center' }}>
            <LinkWithIconComponent
              href={footerLink || undefined}
              iconSize="1rem"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '1rem',
                fontWeight: 400,
                color: '#162F3B',
              }}
            >
              {footer?.replace('{CURRENT_YEAR}', new Date().getFullYear())}
            </LinkWithIconComponent>
          </Box>
        ) : null}
      </Box>
    </ScrollFadeSection>
  );
};

export default ThreeColumnCardComponent;
